import { useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getCurrentBillingCycle } from './useCards';

/**
 * Automatically creates a notification when a card invoice is due within 3 days
 * and there is still a pending balance to pay.
 * Runs once per user session to avoid spamming — and checks the DB to avoid
 * duplicate notifications on the same day.
 */
export const useCardDueAlerts = () => {
  const { user } = useAuth();
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    if (!user || hasCheckedRef.current) return;
    hasCheckedRef.current = true;

    const checkDueAlerts = async () => {
      // 1. Fetch all active, non-archived cards
      const { data: cards } = await supabase
        .from('cards')
        .select('id, name, due_day, closing_day, limit_amount, color')
        .eq('user_id', user.id)
        .eq('is_archived', false)
        .eq('is_active', true);

      if (!cards || cards.length === 0) return;

      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];

      for (const card of cards) {
        // 2. Calculate days until due date
        const day = today.getDate();
        const daysToDue =
          card.due_day >= day
            ? card.due_day - day
            : 30 - day + card.due_day;

        // Only trigger alert for cards due within 3 days
        if (daysToDue > 3) continue;

        // 3. Check if there is a real pending invoice this cycle
        const { start } = getCurrentBillingCycle(card.closing_day);
        const cycleStart = start.toISOString().split('T')[0];

        const { data: txs } = await supabase
          .from('transactions')
          .select('amount')
          .eq('card_id', card.id)
          .eq('user_id', user.id)
          .eq('type', 'despesa')
          .is('deleted_at', null) // ← CRÍTICO: excluir soft-deleted do cálculo da fatura
          .gte('date', cycleStart);

        const currentInvoice = (txs ?? []).reduce(
          (s: number, t: any) => s + Number(t.amount),
          0
        );

        if (currentInvoice <= 0) continue; // No invoice to pay

        // 4. Subtract payments already made in this cycle
        const { data: payments } = await supabase
          .from('card_payments')
          .select('amount')
          .eq('card_id', card.id)
          .eq('user_id', user.id)
          .gte('payment_date', cycleStart);

        const totalPaid = (payments ?? []).reduce(
          (s: number, p: any) => s + Number(p.amount),
          0
        );

        const pending = Math.max(0, currentInvoice - totalPaid);

        if (pending <= 0) continue; // Invoice already fully paid — no need to alert

        // 5. Avoid duplicate: check if this card already got a due-reminder today
        const { data: existing } = await supabase
          .from('notifications')
          .select('id')
          .eq('user_id', user.id)
          .eq('type', 'card_due_reminder')
          .gte('created_at', `${todayStr}T00:00:00.000Z`)
          .limit(50);

        const alreadyNotified = (existing ?? []).some((n: any) => {
          try {
            const d = typeof n.data === 'string' ? JSON.parse(n.data) : n.data;
            return d?.card_id === card.id;
          } catch {
            return false;
          }
        });

        if (alreadyNotified) continue;

        // 6. Create the notification
        const daysText =
          daysToDue === 0
            ? 'hoje'
            : daysToDue === 1
            ? 'amanhã'
            : `em ${daysToDue} dias`;

        const pendingFmt = pending.toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        });

        await supabase.from('notifications').insert({
          user_id: user.id,
          type: 'card_due_reminder',
          title: `Fatura do ${card.name} vence ${daysText}`,
          body: `Valor pendente: ${pendingFmt}. Pague antes do vencimento para evitar juros.`,
          data: {
            card_id: card.id,
            card_name: card.name,
            pending_amount: pending,
            days_to_due: daysToDue,
            cycle_start: cycleStart,
          },
          read_at: null,
        });
      }
    };

    // Small delay to avoid running on the very first render before auth settles
    const timer = setTimeout(checkDueAlerts, 2000);
    return () => clearTimeout(timer);
  }, [user]);
};
